import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ErrorMessages, type Question, Rules } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  checkAllFieldsHaveValues,
  getErrorMessage,
  groupValidation,
  objectToString,
  stringToObject,
  sumRow,
} from '../../utils/form-utils';
import GradeInput from './DateInputField/GradeInput';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { GradeInputWrapper } from './DateInputField/GradeInputField';
import {
  dateIsEmpty,
  dateIsFullyPopulated,
  getMinYearValue,
  invalidDateErrorMessage,
  isMinYearRuleValid,
  isValidDate,
} from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';
interface GradeInputProps {
  name: string;
  defaultValue?: string;
  disabled?: boolean;
  anchorId?: string;
  onChange?(value: string): void;
}

export const getRows = () => ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
export const getColumns = () => ['Male', 'Female', 'Other', 'Undisclosed', 'Total'];
export const getKeys = () =>
  getRows().flatMap((row) => getColumns().map((column) => `${row}${column}`));

export function GenderInputWrapperColumn({
  name,
  defaultValue,
  disabled = false,
  anchorId = useId(),
  onChange,
}: GradeInputProps) {
  const generateInputId = (suffix: string) => `${anchorId}-${suffix}`;

  const initialValues = useMemo(() => stringToObject(defaultValue), [defaultValue]);

  const [values, setValues] = useState(initialValues);
  const [rowTotals, setRowTotals] = useState<Record<string, number>>({});

  const triggerChange = (updatedValues: typeof values) => {
    if (onChange) {
      const valueString = objectToString(updatedValues);
      if (dateIsEmpty(valueString)) {
        onChange('');
      } else {
        onChange(valueString);
      }
    }
  };

  // Type the field argument to only accept valid keys of 'values'
  const handleValueChange = (field: keyof typeof values) => (newValue: string) => {
    const updatedValues = { ...values, [field]: newValue };
    const rows = ['scs2', 'scs1', 'grade6', 'grade7', 'seo', 'heo', 'eo', 'ao', 'aa'];
    const rowTotals = rows.reduce(
      (acc, row) => {
        const total = sumRow(row, updatedValues, 'Total');
        acc[`${row}Total`] = total && total !== 0 ? total.toString() : '';
        return acc;
      },
      {} as Record<string, string>,
    );
    // Add totals to the updatedValues
    const updatedValuesWithTotals = { ...updatedValues, ...rowTotals };
    setValues(updatedValuesWithTotals);
    triggerChange(updatedValuesWithTotals);
  };

  const gradeRows = useMemo(
    () => [
      {
        grade: 'SCS 2',
        fields: ['scs2Male', 'scs2Female', 'scs2Other', 'scs2Undisclosed', 'scs2Total'],
      },
      {
        grade: 'SCS 1',
        fields: ['scs1Male', 'scs1Female', 'scs1Other', 'scs1Undisclosed', 'scs1Total'],
      },
      {
        grade: 'Grade 6',
        fields: ['grade6Male', 'grade6Female', 'grade6Other', 'grade6Undisclosed', 'grade6Total'],
      },
      {
        grade: 'Grade 7',
        fields: ['grade7Male', 'grade7Female', 'grade7Other', 'grade7Undisclosed', 'grade7Total'],
      },
      { grade: 'SEO', fields: ['seoMale', 'seoFemale', 'seoOther', 'seoUndisclosed', 'seoTotal'] },
      { grade: 'HEO', fields: ['heoMale', 'heoFemale', 'heoOther', 'heoUndisclosed', 'heoTotal'] },
      { grade: 'EO', fields: ['eoMale', 'eoFemale', 'eoOther', 'eoUndisclosed', 'eoTotal'] },
      { grade: 'AO', fields: ['aoMale', 'aoFemale', 'aoOther', 'aoUndisclosed', 'aoTotal'] },
      { grade: 'AA', fields: ['aaMale', 'aaFemale', 'aaOther', 'aaUndisclosed', 'aaTotal'] },
    ],
    [],
  );

  return (
    <div>
      <table className="govuk-table">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Grade
            </th>
            <th scope="col" className="govuk-table__header">
              Male
            </th>
            <th scope="col" className="govuk-table__header">
              Female
            </th>
            <th scope="col" className="govuk-table__header">
              Other
            </th>
            <th scope="col" className="govuk-table__header">
              Undisclosed
            </th>
            <th scope="col" className="govuk-table__header">
              Grade totals
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {gradeRows.map(({ grade, fields }) => (
            <tr key={grade} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {grade}
              </th>
              {fields.map((field) => (
                <td key={field} className="govuk-table__cell">
                  <GradeInputColumn
                    testId={`${field}-${grade.toLowerCase().replace(' ', '-')}`}
                    disabled={field.endsWith('Total') ? true : disabled}
                    anchorId={generateInputId(field)}
                    onChange={handleValueChange(field as keyof typeof values)} // Cast field to a valid key
                    value={values[field as keyof typeof values]} // Cast field to a valid key
                  />
                </td>
              ))}
              {/* <td key={`total-${grade.toLowerCase().replace(' ', '-')}`} className='govuk-table__cell'>
                                <GradeInputColumn
                                    testId={`${grade.toLowerCase().replace(' ', '-')}-total`}
                                    disabled={true}
                                    value={getRowTotal(grade.toLowerCase().replace(' ', ''))}
                                />
                            </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface GradeInputFieldProps {
  question: Question;
  disabled?: boolean;
  anchorId: string;
  detailsHeading?: string;
  detailsContent?: string;
}

export function GenderInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: GradeInputFieldProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { data } = useFormData();
  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        checkAllFieldsHaveValues({
          value: stringToObject(value),
          keys: getKeys().filter((key) => !key.endsWith('Total')),
        }),
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: getKeys().filter((key) => key.endsWith('Total')),
        }),
      ]),
  });

  const handleChange = (dateChange: string) => {
    onChange({ target: { name, value: dateChange } });
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}
      <div className="govuk-date-input">
        <GenderInputWrapperColumn
          name={question.name}
          disabled={disabled}
          anchorId={anchorId}
          onChange={handleChange}
          defaultValue={getValues(question.name)}
        />
      </div>
    </>
  );
}
